import { Outlet, Navigate } from "react-router";
import { CircularProgress, Box } from "@mui/material";
import PropTypes from "prop-types";
import UserContext from "../contexts/UserContext.js";
import { useContext, useEffect, useRef, useState } from "react";
import { ConsentProvider } from "../contexts/ConsentContextProvider.jsx";
import { PORTAL } from "../theme/portals.js";
import PortalAccessDenied from "../components/errors/PortalAccessDenied.jsx";

function LoadingIndicator() {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "60vh",
      }}
    >
      <CircularProgress />
    </Box>
  );
}

/**
 * @param {string} [portal] - The portal this app is (a `PORTAL` value). When
 *   given, a signed-in user whose `portals` does not include it sees
 *   {@link PortalAccessDenied} instead of the app. The session is shared
 *   across both hostnames, so this has to be checked here - on every
 *   protected route, not just after signing in on this portal - since a user
 *   already signed in elsewhere never goes through this portal's login.
 *
 *   A stored user from before `portals` existed on that object has it
 *   missing rather than empty; that is treated as unknown, not denied, and
 *   refetched before deciding.
 */
export default function ProtectedRoute({ portal }) {
  const userContext = useContext(UserContext);
  const user = userContext?.user;
  const portals_known = Array.isArray(user?.portals);
  const [refreshed_portals, setRefreshedPortals] = useState(null);
  const has_requested_refresh = useRef(false);

  useEffect(() => {
    if (!user || !portal || portals_known || has_requested_refresh.current) {
      return;
    }
    has_requested_refresh.current = true;
    userContext.refreshUser().then((refreshed) => {
      setRefreshedPortals(refreshed?.portals ?? []);
    });
  }, [user, portal, portals_known, userContext]);

  if (userContext?.loading) {
    return <LoadingIndicator />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!portal) {
    return (
      <ConsentProvider>
        <Outlet />
      </ConsentProvider>
    );
  }

  const portals = portals_known ? user.portals : refreshed_portals;

  if (portals === null) {
    return <LoadingIndicator />;
  }

  if (!portals.includes(portal)) {
    return <PortalAccessDenied portal={portal} portals={portals} />;
  }

  return (
    <ConsentProvider>
      <Outlet />
    </ConsentProvider>
  );
}

ProtectedRoute.propTypes = {
  portal: PropTypes.oneOf(Object.values(PORTAL)),
};
